import { 
  Component, 
  inject, 
  ElementRef, 
  OnDestroy, 
  signal, 
  computed,
  viewChild,
  afterNextRender,
  effect
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DrawerSearchPanelService } from '../../../services/drawer-search-panel.service';
import { SearchPanelComponent } from '../search-panel/search-panel.component';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';


@Component({
  selector: 'app-search-panel-drawer',
  imports: [CommonModule, SearchPanelComponent, MatIcon, MatIconButton],
  templateUrl: './search-panel-drawer.component.html',
  styleUrl: './search-panel-drawer.component.scss',
  host: {
    '(document:mousemove)': 'onMouseMove($event)',
    '(document:mouseup)': 'onMouseUp()',
    '(window:resize)': 'adjustPanelOnWindowResize()'
  }
})
export class SearchPanelDrawerComponent implements OnDestroy {
  drawer = inject(DrawerSearchPanelService);
  private el = inject(ElementRef);


  private panelElement = viewChild<ElementRef<HTMLElement>>('sourcesPanel');


  private resizing = signal(false);
  private startX = signal(0);
  private startWidth = signal(0);
  private screenWidth = signal(window.screen.width);
  private screenHeight = signal(window.screen.height);


  private readonly ABSOLUTE_MIN_WIDTH = 350;
  private readonly ABSOLUTE_MAX_WIDTH = 1400;
  private readonly MIN_CONTENT_PERCENTAGE = 0.30;



  private maxAllowedWidth = computed(() => {
    const viewportWidth = window.innerWidth;
    const maxPercentage = 1 - this.MIN_CONTENT_PERCENTAGE;
    const maxFromViewport = Math.floor(viewportWidth * maxPercentage);
    return Math.min(maxFromViewport, this.ABSOLUTE_MAX_WIDTH);
  });


  private minWidth = computed(() => {
    const viewportWidth = window.innerWidth;
    if (viewportWidth < 480) {
      return Math.max(240, Math.floor(viewportWidth * 0.70));
    }
    return this.ABSOLUTE_MIN_WIDTH;
  });


  constructor() {
    afterNextRender(() => {
      this.adjustPanelOnWindowResize();
      document.body.style.overflowX = 'hidden';
    });


    effect(() => {
      const panel = this.panelElement();
      const isOpen = this.drawer.state().open;
      if (panel && isOpen) {
        requestAnimationFrame(() => {
          if (panel.nativeElement.isConnected) {
            this.adjustPanelToScreen(panel.nativeElement);
          }
        });
      }
    });
  }


  ngOnDestroy() {
    this.cleanup();
    document.body.style.overflowX = '';
  }


  private cleanup() {
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }
  
  private getPanel(): HTMLElement | null {
    const panelRef = this.panelElement();
    if (panelRef) {
      return panelRef.nativeElement;
    }
    return this.el.nativeElement.querySelector('.sources-panel') as HTMLElement;
  }


  adjustPanelOnWindowResize() {
    const panel = this.getPanel();
    if (!panel) return;


    const currentScreenWidth = window.screen.width;
    const currentScreenHeight = window.screen.height;


    const hasScreenChanged =
      this.screenWidth() !== currentScreenWidth ||
      this.screenHeight() !== currentScreenHeight;


    if (hasScreenChanged) {
      this.screenWidth.set(currentScreenWidth);
      this.screenHeight.set(currentScreenHeight);
    }


    this.adjustPanelToScreen(panel);
  }


  private adjustPanelToScreen(panel: HTMLElement) {
    const currentWidth = panel.getBoundingClientRect().width;
    const maxAllowed = this.maxAllowedWidth();
    const minAllowed = this.minWidth();
    const viewportWidth = window.innerWidth;


    let newWidth = currentWidth;


    if (currentWidth > maxAllowed) {
      newWidth = maxAllowed;
    } else if (currentWidth < minAllowed) {
      newWidth = minAllowed;
    } else if (!panel.style.width || currentWidth === 0) {
      const optimalWidth = Math.floor(viewportWidth * 0.35);
      newWidth = Math.max(minAllowed, Math.min(optimalWidth, maxAllowed));
    }


    panel.style.width = `${newWidth}px`;
    this.drawer.width.set(newWidth);
  }


  onStartResize(ev: MouseEvent) {
    ev.preventDefault();
    ev.stopPropagation();


    this.resizing.set(true);
    this.startX.set(ev.clientX);


    const panel = this.getPanel();
    if (!panel) return;


    this.startWidth.set(panel.getBoundingClientRect().width);


    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.body.style.overflowX = 'hidden';
  }



  onMouseMove(ev: MouseEvent) {
    if (!this.resizing()) return;


    const panel = this.getPanel();
    if (!panel) return;


    ev.preventDefault();


    const deltaX = this.startX() - ev.clientX;
    let newWidth = this.startWidth() + deltaX;


    const maxWidth = this.maxAllowedWidth();
    const minWidthValue = this.minWidth();


    newWidth = Math.max(minWidthValue, Math.min(maxWidth, newWidth));
    panel.style.width = `${newWidth}px`;
    this.drawer.width.set(newWidth);



    requestAnimationFrame(() => {
      const hasHorizontalScroll = document.documentElement.scrollWidth > window.innerWidth;
      if (hasHorizontalScroll && panel.style.width) {
        const currentPanelWidth = panel.getBoundingClientRect().width;
        const overflow = document.documentElement.scrollWidth - window.innerWidth;
        const correctedWidth = currentPanelWidth - overflow - 10;
        panel.style.width = `${Math.max(minWidthValue, correctedWidth)}px`;
        this.drawer.width.set(Math.max(minWidthValue, correctedWidth));
      }
    });
  }


  onMouseUp() {
    if (!this.resizing()) return;


    this.resizing.set(false);


    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    document.body.style.overflowX = 'hidden';
  }


 
  close() {
    this.drawer.close();
  }
}
